/**
 * CrawlResultsReview - Display crawl results with grouping and selection
 * Phase 4: Frontend UI Components
 *
 * Features:
 * - Fetches crawl_results when job is complete
 * - Groups pages by URL path prefix (using grouping algorithm)
 * - Shows confidence scores as color-coded badges
 * - Checkbox selection for individual pages
 * - Bulk actions: "Add All X Pages", "Add Selected (Y/X)"
 * - WCAG 2.1 Level AA compliant (keyboard navigation, ARIA labels, focus indicators)
 */

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Check, ExternalLink, ChevronDown, ChevronRight } from "lucide-react";
import { docsClient, authClient } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { CrawlResult, PageGroup } from "@/types/crawl";
import { groupPagesByPath, getConfidenceBadgeVariant, formatConfidence } from "@/lib/crawl-utils";
import { generateDocumentHash } from "@/lib/hash";

interface CrawlResultsReviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  onDocumentsAdded: () => void; // Called after documents are added to refresh parent list
}

export default function CrawlResultsReview({
  open,
  onOpenChange,
  jobId,
  onDocumentsAdded,
}: CrawlResultsReviewProps) {
  const [results, setResults] = useState<CrawlResult[]>([]);
  const [pageGroups, setPageGroups] = useState<PageGroup[]>([]);
  const [selectedUrls, setSelectedUrls] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [addingAll, setAddingAll] = useState(false);
  const [addingSelected, setAddingSelected] = useState(false);
  const { toast } = useToast();

  // Fetch crawl results when modal opens
  useEffect(() => {
    if (!open || !jobId) return;

    const fetchResults = async () => {
      setLoading(true);

      try {
        const { data, error } = await docsClient
          .from("crawl_results")
          .select("*")
          .eq("job_id", jobId)
          .order("confidence", { ascending: false });

        if (error) throw error;

        setResults(data as CrawlResult[]);

        // Group pages by path
        const groups = groupPagesByPath(data as CrawlResult[]);
        setPageGroups(groups);

        // Auto-expand first group
        if (groups.length > 0) {
          setExpandedGroups(new Set([groups[0].name]));
        }

        // Auto-select all pages initially
        const allUrls = new Set(data.map((r: CrawlResult) => r.url));
        setSelectedUrls(allUrls);
      } catch (err) {
        console.error("Error fetching crawl results:", err);
        toast({
          title: "Error",
          description: "Failed to fetch crawl results",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [open, jobId, toast]);

  // Toggle group expansion
  const toggleGroup = (groupName: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  // Toggle individual page selection
  const togglePageSelection = (url: string) => {
    setSelectedUrls((prev) => {
      const next = new Set(prev);
      if (next.has(url)) {
        next.delete(url);
      } else {
        next.add(url);
      }
      return next;
    });
  };

  // Toggle all pages in a group
  const toggleGroupSelection = (group: PageGroup) => {
    const groupUrls = group.pages.map((p) => p.url);
    const allSelected = groupUrls.every((url) => selectedUrls.has(url));

    setSelectedUrls((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        // Deselect all in group
        groupUrls.forEach((url) => next.delete(url));
      } else {
        // Select all in group
        groupUrls.forEach((url) => next.add(url));
      }
      return next;
    });
  };

  // Add a single document
  const addDocument = async (result: CrawlResult): Promise<boolean> => {
    try {
      const {
        data: { user },
      } = await authClient.auth.getUser();

      if (!user) throw new Error("Not authenticated");

      // Check if URL already exists
      const { data: existing } = await docsClient
        .from("documents")
        .select("id, title")
        .eq("user_id", user.id)
        .eq("url", result.url)
        .single();

      if (existing) {
        // Already exists, skip silently
        return false;
      }

      // Generate hash and insert document
      const hash = await generateDocumentHash(result.url);

      const { data: insertedDoc, error } = await docsClient
        .from("documents")
        .insert({
          url: result.url,
          title: result.title || result.url,
          description: result.description,
          tags: result.tags,
          user_id: user.id,
          hash: hash,
          processing_status: "pending",
        })
        .select()
        .single();

      if (error) throw error;

      // Trigger background processing
      if (insertedDoc) {
        const {
          data: { session },
        } = await authClient.auth.getSession();

        fetch(`${import.meta.env.VITE_DOCS_SUPABASE_URL}/functions/v1/process-document`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            document_id: insertedDoc.id,
          }),
        }).catch((err) => {
          console.error("Background processing failed:", err);
        });
      }

      return true;
    } catch (error) {
      console.error("Error adding document:", error);
      return false;
    }
  };

  // Add all selected documents
  const handleAddSelected = async () => {
    setAddingSelected(true);

    const selectedResults = results.filter((r) => selectedUrls.has(r.url));
    let added = 0;
    let skipped = 0;

    for (const result of selectedResults) {
      const success = await addDocument(result);
      if (success) {
        added++;
      } else {
        // Either duplicate or error - count as skipped
        skipped++;
      }
    }

    toast({
      title: "Documents Added",
      description: `Added ${added} documents. ${skipped > 0 ? `Skipped ${skipped} (duplicates or errors).` : ""}`,
    });

    setAddingSelected(false);
    onDocumentsAdded();
    onOpenChange(false);
  };

  // Add all documents (ignore selection)
  const handleAddAll = async () => {
    setAddingAll(true);

    let added = 0;
    let skipped = 0;

    for (const result of results) {
      const success = await addDocument(result);
      if (success) {
        added++;
      } else {
        skipped++;
      }
    }

    toast({
      title: "All Documents Added",
      description: `Added ${added} documents. ${skipped > 0 ? `Skipped ${skipped} (duplicates or errors).` : ""}`,
    });

    setAddingAll(false);
    onDocumentsAdded();
    onOpenChange(false);
  };

  const selectedCount = selectedUrls.size;
  const totalCount = results.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl h-[85vh] flex flex-col p-0"
        aria-describedby="crawl-results-description"
      >
        <DialogHeader className="px-6 pt-6 pb-4 shrink-0 border-b">
          <DialogTitle>Review Crawl Results</DialogTitle>
          <DialogDescription id="crawl-results-description">
            Found {totalCount} relevant page{totalCount !== 1 ? "s" : ""}. Select pages to add to
            your library.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center flex-1">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : results.length === 0 ? (
          <div className="flex flex-col items-center justify-center flex-1 text-muted-foreground">
            <p>No relevant pages found in this crawl.</p>
          </div>
        ) : (
          <>
            {/* Results List */}
            <ScrollArea className="flex-1 px-6">
              <div className="space-y-4 py-4">
                {pageGroups.map((group) => {
                  const isExpanded = expandedGroups.has(group.name);
                  const groupUrls = group.pages.map((p) => p.url);
                  const selectedInGroup = groupUrls.filter((url) => selectedUrls.has(url)).length;
                  const allSelected = selectedInGroup === group.pages.length;
                  const someSelected = selectedInGroup > 0 && selectedInGroup < group.pages.length;

                  return (
                    <div key={group.name} className="border rounded-lg">
                      {/* Group Header */}
                      <div
                        className="flex items-center gap-3 p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => toggleGroup(group.name)}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                        aria-label={`${group.name} group, ${group.pages.length} pages, ${formatConfidence(group.confidence_avg)} average confidence`}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleGroup(group.name);
                          }
                        }}
                      >
                        <Checkbox
                          checked={allSelected || someSelected}
                          indeterminate={someSelected}
                          onCheckedChange={(checked) => {
                            toggleGroupSelection(group);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Select all pages in ${group.name} group`}
                        />

                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold">{group.name}</h3>
                            <Badge variant="outline" className="text-xs">
                              {group.pages.length} page{group.pages.length !== 1 ? "s" : ""}
                            </Badge>
                            <Badge
                              variant={getConfidenceBadgeVariant(group.confidence_avg)}
                              className="text-xs"
                            >
                              {formatConfidence(group.confidence_avg)} avg
                            </Badge>
                          </div>
                          {selectedInGroup > 0 && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {selectedInGroup} of {group.pages.length} selected
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Group Pages (Expandable) */}
                      {isExpanded && (
                        <div className="border-t divide-y">
                          {group.pages.map((page) => {
                            const isSelected = selectedUrls.has(page.url);

                            return (
                              <div
                                key={page.id}
                                className="flex items-start gap-3 p-4 hover:bg-muted/30 transition-colors"
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => togglePageSelection(page.url)}
                                  aria-label={`Select ${page.title || page.url}`}
                                  className="mt-1"
                                />

                                <div className="flex-1 min-w-0 space-y-2">
                                  <div>
                                    <div className="flex items-start gap-2 flex-wrap">
                                      <h4 className="font-medium text-sm">
                                        {page.title || "Untitled Page"}
                                      </h4>
                                      <Badge
                                        variant={getConfidenceBadgeVariant(page.confidence)}
                                        className="text-xs"
                                      >
                                        {formatConfidence(page.confidence)}
                                      </Badge>
                                    </div>
                                    {page.description && (
                                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                        {page.description}
                                      </p>
                                    )}
                                  </div>

                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <ExternalLink className="h-3 w-3" />
                                    <a
                                      href={page.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="hover:underline truncate"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {page.url}
                                    </a>
                                  </div>

                                  {page.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                      {page.tags.map((tag) => (
                                        <Badge key={tag} variant="secondary" className="text-xs">
                                          {tag}
                                        </Badge>
                                      ))}
                                    </div>
                                  )}

                                  {page.reasoning && (
                                    <p className="text-xs text-muted-foreground italic">
                                      💡 {page.reasoning}
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            {/* Footer Actions */}
            <DialogFooter className="px-6 py-4 border-t shrink-0">
              <div className="flex flex-col sm:flex-row gap-2 w-full">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="flex-1"
                  disabled={addingAll || addingSelected}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddSelected}
                  disabled={selectedCount === 0 || addingAll || addingSelected}
                  className="flex-1"
                >
                  {addingSelected ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Adding {selectedCount}...
                    </>
                  ) : (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Add Selected ({selectedCount}/{totalCount})
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleAddAll}
                  disabled={addingAll || addingSelected}
                  className="flex-1"
                  variant="default"
                >
                  {addingAll ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Adding All...
                    </>
                  ) : (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Add All {totalCount} Pages
                    </>
                  )}
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
